import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useLocation, Link } from 'react-router-dom';
import { getSurvey, createSurvey, updateSurvey, importDocAsSurvey } from '../lib/api';
import type { SurveyConfig, QuestionType, Question } from '../components/editor/types';
import QuestionCard from '../components/editor/QuestionCard';
import AddQuestionModal from '../components/editor/AddQuestionModal';
import SettingsPanel from '../components/editor/SettingsPanel';
import PreviewPanel from '../components/editor/PreviewPanel';

type Mode = 'new' | 'edit';

type Props = {
  mode: Mode;
};

type Tab = 'questions' | 'preview';

function blankSurvey(id: string): SurveyConfig {
  return {
    id,
    header: { mainTitle: '问卷', subTitle: '' },
    questions: [],
    settings: {},
  };
}

function nextQuestionId(config: SurveyConfig): string {
  const ids = new Set(config.questions.map((q) => q.id));
  let n = 1;
  while (ids.has(`Q${n}`)) n += 1;
  return `Q${n}`;
}

function makeQuestion(type: QuestionType, id: string): Question {
  const base = {
    id,
    type,
    title: '',
    description: '',
    required: true,
    displayLogic: { operator: 'AND' as const, conditions: [] },
  };
  if (type === 'single' || type === 'multi' || type === 'select') {
    return { ...base, options: [{ value: '选项1' }, { value: '选项2' }] };
  }
  if (type === 'slider') {
    return { ...base, params: { min: 0, max: 100, step: 1 } };
  }
  if (type === 'longtext') {
    return { ...base, params: { maxLen: 500 } };
  }
  if (type === 'matrix') {
    return { ...base, params: { rows: ['项目1', '项目2'], columns: ['满意', '一般', '不满意'] } };
  }
  return base;
}

export default function Editor({ mode }: Props) {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  // Word 导入的草稿（AI 解析结果）：进编辑器人工审核，保存才创建
  const imported = mode === 'new' ? (location.state as any)?.importedConfig : null;

  const [config, setConfig] = useState<SurveyConfig>(() =>
    mode === 'new'
      ? (imported ? { ...imported, id: imported.id || `survey-${Date.now().toString(36)}` } : blankSurvey(`survey-${Date.now().toString(36)}`))
      : blankSurvey('loading'),
  );
  const [loadedId, setLoadedId] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('questions');
  const [dirty, setDirty] = useState(!!imported);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);

  // 拖拽排序状态
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);

  // 编辑器内直接导入 Word（也兼容问卷管理页经 sessionStorage 传来的草稿）
  const [wordImporting, setWordImporting] = useState(false);
  const wordFileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (mode !== 'new' || imported) return;
    try {
      const draft = window.sessionStorage.getItem('wenjuan_import_draft');
      if (draft) {
        const parsed = JSON.parse(draft) as SurveyConfig;
        setConfig({ ...parsed, id: parsed.id || `survey-${Date.now().toString(36)}` });
        setDirty(true);
        setSaveMsg('已导入 Word 草稿，请审核后保存');
      }
      window.sessionStorage.removeItem('wenjuan_import_draft');
    } catch { /* 草稿损坏则忽略，从空白问卷开始 */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const handleWordImport = async (file: File) => {
    if (!/\.docx$/i.test(file.name)) { setError('请选择 .docx 文件（旧版 .doc 请先另存为 .docx）'); return; }
    if (file.size > 5 * 1024 * 1024) { setError('Word 文件不能超过 5MB'); return; }
    setWordImporting(true);
    setError(null);
    try {
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => typeof reader.result === 'string' ? resolve(reader.result) : reject(new Error('文件读取失败'));
        reader.onerror = () => reject(new Error('文件读取失败'));
        reader.readAsDataURL(file);
      });
      const result = await importDocAsSurvey(dataUrl);
      setConfig({ ...result.config, id: result.config?.id || `survey-${Date.now().toString(36)}` });
      setLoadedId(null);
      setDirty(true);
      setSaveMsg(result.warnings?.length ? `已导入草稿（${result.warnings.length} 个提示），请审核后保存` : '已导入 Word 草稿，请审核后保存');
      setTab('questions');
    } catch (e: any) {
      const err = e?.body?.error;
      setError(err === 'ai_not_configured' ? '服务器未配置 AI 密钥' : err === 'docx_parse_failed' ? '文档解析失败，请确认是有效的 .docx' : e?.body?.detail || e?.message || 'Word 导入失败');
    } finally {
      setWordImporting(false);
      if (wordFileRef.current) wordFileRef.current.value = '';
    }
  };

  useEffect(() => {
    if (mode !== 'edit' || !id) return;
    getSurvey(id)
      .then((data) => {
        setConfig(data.config);
        setLoadedId(data.config.id);
      })
      .catch((e: Error) => setError(e.message));
  }, [mode, id]);

  const updateConfig = (patch: Partial<SurveyConfig>) => {
    setConfig((c) => ({ ...c, ...patch }));
    setDirty(true);
  };

  const updateQuestion = (qid: string, patch: Partial<Question>) => {
    setConfig((c) => ({
      ...c,
      questions: c.questions.map((q) => (q.id === qid ? { ...q, ...patch } : q)),
    }));
    setDirty(true);
  };

  const addQuestion = (type: QuestionType) => {
    setConfig((c) => {
      const qid = nextQuestionId(c);
      const q = makeQuestion(type, qid);
      return { ...c, questions: [...c.questions, q] };
    });
    setDirty(true);
  };

  const insertBelow = (qid: string, type: QuestionType) => {
    setConfig((c) => {
      const idx = c.questions.findIndex((q) => q.id === qid);
      if (idx < 0) return c;
      const qidNew = nextQuestionId(c);
      const q = makeQuestion(type, qidNew);
      const qs = [...c.questions];
      qs.splice(idx + 1, 0, q);
      return { ...c, questions: qs };
    });
    setDirty(true);
  };

  const removeQuestion = (qid: string) => {
    setConfig((c) => {
      const qs = c.questions.filter((q) => q.id !== qid);
      const fixed = qs.map((q) => ({
        ...q,
        displayLogic: {
          ...(q.displayLogic ?? { operator: 'AND', conditions: [] }),
          conditions: (q.displayLogic?.conditions ?? []).filter(
            (cond) => cond.question !== qid,
          ),
        },
      }));
      return { ...c, questions: fixed };
    });
    setDirty(true);
  };

  // 拖拽排序
  const handleDragStart = (_e: React.DragEvent, idx: number) => {
    setDragIndex(idx);
  };
  const handleDragOver = (_e: React.DragEvent, idx: number) => {
    if (dragIndex === null) return;
    setOverIndex(idx);
  };
  const handleDragEnd = () => {
    setDragIndex(null);
    setOverIndex(null);
  };
  const handleDrop = (_e: React.DragEvent, idx: number) => {
    if (dragIndex === null || dragIndex === idx) {
      setDragIndex(null);
      setOverIndex(null);
      return;
    }
    setConfig((c) => {
      const qs = [...c.questions];
      const [moved] = qs.splice(dragIndex, 1);
      qs.splice(idx, 0, moved);
      return { ...c, questions: qs };
    });
    setDirty(true);
    setDragIndex(null);
    setOverIndex(null);
  };

  const handleSave = async (): Promise<boolean> => {
    setSaving(true);
    setSaveMsg(null);
    try {
      if (mode === 'new' || !loadedId) {
        await createSurvey(config);
        setLoadedId(config.id);
        setSaveMsg('已创建');
        navigate(`/editor/${encodeURIComponent(config.id)}`, { replace: true });
      } else {
        await updateSurvey(loadedId, config);
        setSaveMsg('已保存');
      }
      setDirty(false);
      return true;
    } catch (e: any) {
      const body = e?.body;
      if (body?.error === 'invalid_config') setSaveMsg('保存失败，请检查题目内容');
      else if (body?.error === 'conflict') setSaveMsg('ID 已存在，请改 id');
      else setSaveMsg(e?.message || '保存失败');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (saving) return;
    if (dirty || mode === 'new' || !loadedId) {
      const saved = await handleSave();
      if (!saved) return;
    }
    setPublishOpen(true);
  };

  return (
    <div className="editor-layout">
      <header className="topbar">
        <Link to="/" className="editor-back">← 返回问卷管理</Link>
        <Link to="/" className="brand">
          <div className="brand-mark">问</div>
          <div className="brand-name">编辑器</div>
        </Link>
        <div className="topbar-spacer" />

        <div className="editor-tabs" style={{ display: 'flex', gap: 4 }}>
          <button
            className={`q-chip ${tab === 'questions' ? 'is-on' : ''}`}
            onClick={() => setTab('questions')}
          >
            题目（{config.questions.length}）
          </button>
          <button
            className={`q-chip ${tab === 'preview' ? 'is-on' : ''}`}
            onClick={() => setTab('preview')}
          >
            预览
          </button>
        </div>

        <button
          className="btn"
          onClick={() => wordFileRef.current?.click()}
          disabled={wordImporting}
          title="上传 .docx，AI 解析为问卷草稿（覆盖当前编辑内容，保存后生效）"
        >
          {wordImporting ? 'AI 解析中…' : '⬆ 导入 Word'}
        </button>
        <input
          ref={wordFileRef}
          type="file"
          accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          hidden
          onChange={(e) => { const f = e.target.files?.[0]; if (f) void handleWordImport(f); }}
        />

        <button
          className="btn btn-primary"
          onClick={() => { void handleSave(); }}
          disabled={saving || !dirty}
          title={dirty ? '保存到 DB' : '无改动'}
        >
          {saving ? '保存中…' : dirty ? '保存' : '已保存'}
        </button>
        <button className="btn btn-publish" onClick={() => { void handlePublish(); }} disabled={saving}>
          ✈ 发布
        </button>

        {saveMsg && <span className="save-msg">{saveMsg}</span>}

        {/* 设置按钮：顶栏最右 */}
        <button
          className="icon-circle"
          onClick={() => setSettingsOpen(true)}
          title="设置"
          aria-label="设置"
        >
          <IconGear />
        </button>
      </header>

      {error && <div className="editor-error">加载失败：{error}</div>}
      {imported && (
        <div className="editor-error" style={{ background: 'var(--accent-soft-2, #eef4ff)', color: 'var(--accent-strong, #2f63d8)' }}>
          已由 Word 文档 AI 解析生成草稿，请逐题核对（题型/选项/分值/前置页/签名设置），确认无误后点「保存」正式创建。
        </div>
      )}

      {tab === 'questions' && (
        <main className="editor-page">
          <div className="editor-card">
            <div className="editor-head">
              <div className="editor-head-main">
                <input
                  className="editor-title"
                  value={config.header?.mainTitle ?? ''}
                  onChange={(e) =>
                    updateConfig({ header: { ...config.header, mainTitle: e.target.value } })
                  }
                  placeholder="问卷标题"
                />
                <input
                  className="editor-desc"
                  value={config.header?.subTitle ?? ''}
                  onChange={(e) =>
                    updateConfig({ header: { ...config.header, subTitle: e.target.value } })
                  }
                  placeholder="请输入描述"
                />
              </div>
            </div>

            <div className="q-list">
              {config.questions.map((q, idx) => (
                <QuestionCard
                  key={q.id}
                  question={q}
                  index={idx}
                  allQuestions={config.questions}
                  onChange={(patch) => updateQuestion(q.id, patch)}
                  onRemove={() => removeQuestion(q.id)}
                  onInsertBelow={(t) => insertBelow(q.id, t)}
                  draggable
                  onDragStart={handleDragStart}
                  onDragOver={handleDragOver}
                  onDragEnd={handleDragEnd}
                  onDrop={handleDrop}
                  isDragOver={overIndex === idx && dragIndex !== null && dragIndex !== idx}
                />
              ))}

              <div className="q-add">
                <button
                  type="button"
                  className="q-add-btn"
                  onClick={() => setAddOpen(true)}
                >
                  + 添加题目
                </button>
              </div>

            </div>
          </div>

          {addOpen && (
            <AddQuestionModal
              onSelect={(t) => {
                setAddOpen(false);
                addQuestion(t);
              }}
              onClose={() => setAddOpen(false)}
            />
          )}
        </main>
      )}

      {tab === 'preview' && (
        <main className="editor-page">
          <div className="editor-card">
            <PreviewPanel config={config} />
          </div>
        </main>
      )}

      {settingsOpen && (
        <SettingsPanel
          config={config}
          onChange={updateConfig}
          onClose={() => setSettingsOpen(false)}
        />
      )}
      {publishOpen && (
        <PublishModal
          surveyId={config.id}
          onClose={() => setPublishOpen(false)}
        />
      )}
    </div>
  );
}

function PublishModal({ surveyId, onClose }: { surveyId: string; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const link = `${window.location.origin}/s/${encodeURIComponent(surveyId)}`;
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(link)}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="record-modal publish-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-title">
          <div>
            <div className="eyebrow">PUBLISH QUESTIONNAIRE</div>
            <h2>发布成功</h2>
          </div>
          <button className="modal-close" onClick={onClose} aria-label="关闭">×</button>
        </div>
        <p className="publish-hint">复制填写链接，或让填写者扫描二维码。</p>
        <label className="publish-label">填写链接</label>
        <div className="publish-link-row">
          <input className="input" value={link} readOnly onFocus={(e) => e.currentTarget.select()} />
          <button className="btn btn-primary" onClick={() => { void copyLink(); }}>{copied ? '已复制' : '复制链接'}</button>
        </div>
        <div className="publish-qr-block">
          <div className="publish-label">二维码</div>
          <div className="publish-qr-frame"><img src={qrUrl} alt="问卷填写二维码" /></div>
        </div>
      </div>
    </div>
  );
}

function IconGear() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h.01a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51h.01a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v.01a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}